import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Chapter, SectionHead } from "@/components/Reveal";
import { JoinCta } from "@/components/Buttons";
import { getStats } from "@/lib/api";
import { Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const ROAD_IMG =
  "https://images.unsplash.com/photo-1625556841726-ee3959350033?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhdXRvbm9tb3VzJTIwZHJpdmluZyUyMHZpZXclMjBjaXR5JTIwc3RyZWV0fGVufDB8fHx8MTc4OTQ1ODQwOXww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { n: "01", title: "Join free", text: "Reserve your place in the Founding Network in under a minute." },
  { n: "02", title: "Use your phone", text: "No special hardware. Your smartphone is the sensor." },
  { n: "03", title: "Capture real-world observations", text: "Drive or walk - the network observes roads, signs and infrastructure as you move." },
  { n: "04", title: "Earn network rewards when live", text: "When the Pilot network opens, verified contributions become part of the network's reward system." },
];

const GENESIS_POINTS = [
  "Join for free",
  "Reserve your Founding position",
  "Get early access when the Pilot network opens",
];

const PhoneVisual = () => (
  <div className="relative mx-auto w-[260px] sm:w-[300px]" data-testid="contributor-visual">
    <div className="absolute -inset-10 rounded-full bg-signal-cyan/[0.05] blur-[80px] pointer-events-none" />
    <div className="relative rounded-[2.4rem] border border-white/10 bg-ink-900 p-2.5 shadow-[0_40px_100px_rgba(0,0,0,0.5)]">
      <div className="relative overflow-hidden rounded-[1.9rem] aspect-[9/19]">
        <img src={ROAD_IMG} alt="Phone capturing a city street" className="absolute inset-0 h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-ink-950/40" />
        <div className="absolute left-0 right-0 h-px bg-signal-cyan/50 scan-line" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="absolute left-[16%] bottom-[22%] w-[38%] rounded border border-signal-coral/80"
        >
          <span className="absolute -top-5 left-0 text-[10px] font-medium text-signal-coral whitespace-nowrap">
            Pothole · 97%
          </span>
          <span className="absolute inset-x-0 bottom-0 h-6" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute right-[12%] top-[24%] h-14 w-12 rounded border border-signal-green/80"
        >
          <span className="absolute -top-5 left-0 text-[10px] font-medium text-signal-green whitespace-nowrap">
            Traffic sign
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 2, duration: 0.8 }}
          className="absolute left-[10%] top-[52%] text-[10px] font-medium text-signal-cyan"
        >
          Lane marking
          <span className="block mt-1 h-px w-20 bg-signal-cyan/70" />
        </motion.div>
        <div className="absolute bottom-3 left-3 font-mono text-[8px] tracking-[0.18em] text-zinc-500 uppercase">
          Concept visualization
        </div>
      </div>
    </div>
  </div>
);

export const Contributors = () => {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    getStats()
      .then((s) => setMembers(s.genesis_members))
      .catch(() => setMembers(null));
  }, []);

  return (
    <>
      <section id="contribute" className="relative py-28 sm:py-40 bg-ink-900/40" data-testid="section-contribute">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Chapter label="Contribute" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6">
              <SectionHead
                title={"Contribute real-world data."}
                sub="The network is built by people moving through the world. Here is what you actually do."
              />
              <div className="mt-14">
                {STEPS.map((s, i) => (
                  <Reveal key={s.n} delay={0.08 * i} y={14}>
                    <div
                      className={`py-6 ${i > 0 ? "border-t border-white/5" : ""}`}
                      data-testid={`contributor-step-${i + 1}`}
                    >
                      <div className="flex items-baseline gap-5">
                        <span className="text-xs font-semibold tracking-[0.2em] text-signal-cyan">{s.n}</span>
                        <h3 className="font-display text-xl font-semibold tracking-tight text-mist">{s.title}</h3>
                      </div>
                      <p className="mt-2 pl-11 text-[15px] leading-relaxed text-steel max-w-md">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.35}>
                <div className="mt-10">
                  <JoinCta testid="contributor-join-btn" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.2}>
                <PhoneVisual />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="genesis" className="relative py-28 sm:py-40 bg-ink-950" data-testid="section-genesis">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-6">
              <Chapter label="Founding Network" />
              <SectionHead title={"Join the Founding Network."} />
              <Reveal delay={0.15}>
                <p className="mt-6 text-lg leading-relaxed text-steel max-w-md">
                  You are not joining after the network is built. You are joining
                  while it is being built.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-10">
                  <JoinCta testid="genesis-contributor-join-btn" />
                </div>
              </Reveal>
              {members !== null && (
                <Reveal delay={0.3}>
                  <p className="mt-8 text-sm text-steel" data-testid="genesis-member-count">
                    <span className="font-display text-2xl font-semibold text-mist tabular-nums">{members.toLocaleString()}</span>
                    {" "}contributors have already reserved their position.
                  </p>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <div className="mt-2">
                {GENESIS_POINTS.map((p, i) => (
                  <Reveal key={p} delay={0.1 * i} y={12}>
                    <div className={`flex items-center gap-4 py-5 ${i > 0 ? "border-t border-white/5" : ""}`} data-testid={`genesis-point-${i}`}>
                      <Check className="h-4 w-4 text-signal-green shrink-0" />
                      <span className="text-lg text-mist">{p}</span>
                    </div>
                  </Reveal>
                ))}
                <Reveal delay={0.3} y={12}>
                  <p className="pt-6 border-t border-white/5 text-sm leading-relaxed text-steel">
                    Invite others with your personal link - verified invites move
                    you up the Founding leaderboard and unlock recognition
                    milestones. Founding Points are reputation markers, not
                    currency, and no income is promised.
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
